/**
 * Project storage. A "project" = a named schema + content pair.
 * Persisted to localStorage so the writer doesn't re-upload every visit.
 *
 * Bliksys is stateless on the server; this is browser-local state for
 * convenience. Losing it only means re-uploading, not losing work —
 * content is saved via the deploy target.
 */

import type { AssetOriginConfig, DeployTargetConfig, JsonSchema, JsonValue } from '@bliksys/shared';

const STORAGE_KEY = 'bliksys.projects.v1';
const LAST_OPENED_KEY = 'bliksys.lastOpened.v1';
/**
 * When the user dismisses the demo from the recent-projects list, we
 * record it here so it doesn't reappear on the next visit. Plain
 * localStorage flag — same caveat as the project store: cleared if
 * the user wipes site data, but that's the right granularity for a
 * "don't show me this again" gesture.
 */
const DEMO_DISMISSED_KEY = 'bliksys.demoDismissed.v1';

/** Reserved id for the bundled ACME demo — never persisted. */
export const DEMO_PROJECT_ID = '__demo__';

export function isDemoDismissed(): boolean {
  try {
    return window.localStorage.getItem(DEMO_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissDemo(): void {
  try {
    window.localStorage.setItem(DEMO_DISMISSED_KEY, '1');
  } catch {
    // Storage quota / private-mode / disabled — silent is fine.
  }
}

export interface StoredProject {
  id: string;
  name: string;
  schema: JsonSchema;
  data: JsonValue;
  /** Undefined hides the editor's Deploy button — the user has the
   * dedicated Export button for plain JSON downloads. */
  deployTarget?: DeployTargetConfig | undefined;
  /** Undefined hides the gallery picker on asset-typed widgets — they
   * fall back to a plain URL input. */
  assetOrigin?: AssetOriginConfig | undefined;
  createdAt: string;
  updatedAt: string;
}

/** Patch fields on an existing project. Creates if missing. */
export function updateProject(
  id: string,
  patch: Partial<Omit<StoredProject, 'id' | 'createdAt'>>,
): StoredProject | null {
  const existing = getProject(id);
  if (!existing) return null;
  const updated: StoredProject = {
    ...existing,
    ...patch,
    id: existing.id,
    createdAt: existing.createdAt,
    updatedAt: new Date().toISOString(),
  };
  saveProject(updated);
  return updated;
}

export function listProjects(): StoredProject[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isStoredProject).map(sanitizeProject);
  } catch {
    return [];
  }
}

/**
 * Strip fields that the current shape doesn't support. Important
 * because localStorage entries outlive code changes — older versions
 * of Bliksys shipped with `{ kind: 'download' }` deploy targets, and a
 * stale browser session would otherwise carry that into the new shape
 * (where `download` isn't a valid kind), triggering UI bugs like a
 * Deploy button that shouldn't be there.
 *
 * Belt-and-braces — we don't run a versioned migration because the
 * affected surface is small and the breaking changes are localized.
 */
function sanitizeProject(project: StoredProject): StoredProject {
  let next = project;
  if (next.deployTarget !== undefined && !isCurrentDeployTarget(next.deployTarget)) {
    const { deployTarget: _drop, ...rest } = next;
    next = rest;
  }
  if (next.assetOrigin !== undefined && !isCurrentAssetOrigin(next.assetOrigin)) {
    const { assetOrigin: _drop, ...rest } = next;
    next = rest;
  }
  return next;
}

function isCurrentDeployTarget(target: unknown): boolean {
  if (typeof target !== 'object' || target === null) return false;
  const kind = (target as { kind?: unknown }).kind;
  return kind === 'webhook' || kind === 'github' || kind === 'cloudflareKV';
}

function isCurrentAssetOrigin(origin: unknown): boolean {
  if (typeof origin !== 'object' || origin === null) return false;
  const kind = (origin as { kind?: unknown }).kind;
  return kind === 's3';
}

export function getProject(id: string): StoredProject | null {
  return listProjects().find((p) => p.id === id) ?? null;
}

export function saveProject(project: StoredProject): void {
  const projects = listProjects();
  const index = projects.findIndex((p) => p.id === project.id);
  const updated = { ...project, updatedAt: new Date().toISOString() };
  if (index === -1) {
    projects.push(updated);
  } else {
    projects[index] = updated;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
}

export function deleteProject(id: string): void {
  const projects = listProjects().filter((p) => p.id !== id);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
  if (getLastOpenedId() === id) clearLastOpened();
}

export function getLastOpenedId(): string | null {
  try {
    return window.localStorage.getItem(LAST_OPENED_KEY);
  } catch {
    return null;
  }
}

export function setLastOpenedId(id: string): void {
  try {
    window.localStorage.setItem(LAST_OPENED_KEY, id);
  } catch {
    // Storage quota / private-mode / disabled: silent is correct here.
  }
}

export function clearLastOpened(): void {
  try {
    window.localStorage.removeItem(LAST_OPENED_KEY);
  } catch {
    // Same as above — nothing to do on failure.
  }
}

export function createProjectId(): string {
  // A short, opaque id. crypto.randomUUID() is available in all modern
  // browsers; falls back to a timestamp for the unlikely missing case.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function isStoredProject(v: unknown): v is StoredProject {
  if (typeof v !== 'object' || v === null) return false;
  const p = v as Record<string, unknown>;
  return (
    typeof p.id === 'string' &&
    typeof p.name === 'string' &&
    typeof p.schema === 'object' &&
    p.schema !== null &&
    typeof p.createdAt === 'string' &&
    typeof p.updatedAt === 'string'
    // `data` can be any JsonValue including null, strings, arrays — don't over-check.
  );
}
